import { HealthCardValidatorFactory, HealthCardValidatorRequest } from "@/Common/Components/Patient/HealthCardComponents/HealthCardValidator";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { GeographicInfoState } from "../Models/Localization/GeographicInfoState";
import { CanadaState } from "../Resources/Lookups/Enums/CommonLookupCodes";

class HealthCardHelper {
    /**
     * Array that contains provinces requiring alternate expiration date format.
     */
    public static readonly provincesRequiringAlternateFormat = [
        CanadaState.Newfoundland
    ];

    /**
     * Get valid expiry years from the current year
     * @returns {number[]}
     */
    public static getExpiryYears(): number[] {
        const currentYear = new Date().getFullYear();

        const years: number[] = [];
        for (let i = currentYear - 5; i < currentYear + 30; i++)
            years.push(i);

        return years;
    }

    /**
     * Get valid expiry months
     * @returns {number[]}
     */
    public static getExpiryMonths(): number[] {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    }

    /**
     * Create an empty health card
     * @returns {PatientIdentifier}
     */
    public static createEmptyHealthCard(): PatientIdentifier {
        const healthCard = new PatientIdentifier();

        const defaultCountry = cacheStore.GetDefaultCountryForActiveSite();
        healthCard.ExpiryDate = new Date();
        healthCard.HealthIdentification_id = GuidHelper.NewGuid();
        healthCard.AvailableCountryLookups = cacheStore.GetCountryLookups();
        healthCard.AvailableCountryRegionLookups = cacheStore.GetCountryRegionLookups(defaultCountry);
        healthCard.CountryLookupId = defaultCountry.LookupID;
        healthCard.ProvinceLookupId = cacheStore.GetDefaultRegionForActiveSite().LookupID;
        healthCard.Number = "";
        healthCard.Version = "";

        return healthCard;
    }

    /**
     * Create an empty identifier
     * @returns {PatientIdentifier}
     */
    public static createEmptyOtherIdentifier(): PatientIdentifier {
        const otherIdentifier = new PatientIdentifier();
        otherIdentifier.HealthIdentification_id = GuidHelper.NewGuid();
        return otherIdentifier;
    }

    /**
     * Returns the updated health card expiration date
     * @param {string} provinceLookupId
     * @param {Date} value
     * @returns {Date | null}
     */
    public static updateExpirationDate(provinceLookupId: string, value: Date): Date | null {
        if (!value)
            return null;

        const date = new Date(value);
        let updatedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (!GeographicInfoState.isNewFoundlandProvince(provinceLookupId)) {
            updatedDate = new Date(updatedDate.getFullYear(), updatedDate.getMonth() + 1, 0);
        }

        return updatedDate;
    }

    /**
     * Verify if health card is valid
     * @param {string} healthCardNumber
     * @param {string} regionLookupId
     * @param {string} countryLookupId
     * @param {string} firstName
     * @param {string} lastName
     * @param {Date} dateOfBirth
     * @param {string} genderLookupId
     * @returns {boolean}
     */
    public static isHealthCardValid(
        healthCardNumber: string,
        regionLookupId: string,
        countryLookupId: string,
        firstName: string,
        lastName: string,
        dateOfBirth: Date,
        genderLookupId: string
    ): boolean {
        if (!cacheStore.ClinicSettings.EnableHealthCardValidation) {
            const isOntarioHealthCard = regionLookupId === cacheStore.FindLookupByLookupCode("CanadaState_ON").LookupID;
            if (isOntarioHealthCard)
                return true;

            const validator = new ValidationHelper();
            return validator.validateNAM(
                healthCardNumber,
                countryLookupId,
                regionLookupId,
                firstName,
                lastName,
                dateOfBirth,
                genderLookupId
            );
        }

        let validator = HealthCardValidatorFactory.getValidator(countryLookupId, regionLookupId);
        return validator.isValid(new HealthCardValidatorRequest(
            healthCardNumber,
            firstName,
            lastName,
            dateOfBirth,
            genderLookupId
        ));
    }

    /**
     * Determines if the alternate date format should be shown for expiry date, based on the province.
     * @param {string} stateLookupId
     * @returns {boolean}
     */
    public static requiresAlternateDateFormat(stateLookupId: string): boolean {
        return this.provincesRequiringAlternateFormat.some(
            province => cacheStore.GetLookupIdForProvince(province) === stateLookupId
        );
    }
}

export { HealthCardHelper };
